/**
 * Synchronous in-memory session id for request headers.
 * Updated immediately on createSession/clear — before React re-renders or useEffect persist.
 * CsrfMiddleware reads this first, then falls back to localStorage.
 */
let activeSessionId: string | null = null;

export function setActiveSessionId(sessionId: string | null | undefined): void {
  activeSessionId = sessionId ?? null;
}

export function getActiveSessionId(): string | null {
  return activeSessionId;
}

export function clearActiveSessionId(): void {
  activeSessionId = null;
}

export function readSessionIdFromLocalStorage(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem('session');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { sessionId?: string; session_id?: string };
    return parsed?.sessionId ?? parsed?.session_id ?? null;
  } catch {
    return null;
  }
}

/** Extract session id from API DTO whether camelCase or snake_case. */
export function extractSessionId(
  session: { sessionId?: string; session_id?: string } | null | undefined
): string | null {
  if (!session) return null;
  return session.sessionId ?? session.session_id ?? null;
}

/** Prefer memory store (fresh create), then localStorage. */
export function resolveSessionIdForRequests(): string | null {
  return getActiveSessionId() ?? readSessionIdFromLocalStorage();
}

/** Parse aegis_session_id from Set-Cookie response header(s). */
export function parseSessionIdFromSetCookie(
  setCookieHeader: string | null | undefined
): string | null {
  if (!setCookieHeader) return null;
  const chunks = setCookieHeader.split(/,(?=\s*[A-Za-z_][A-Za-z0-9_-]*=)/);
  for (const chunk of chunks) {
    const firstPart = chunk.split(';')[0]?.trim();
    if (!firstPart) continue;
    const eq = firstPart.indexOf('=');
    if (eq <= 0) continue;
    const name = firstPart.slice(0, eq).trim();
    const value = firstPart.slice(eq + 1).trim();
    if (name === 'aegis_session_id' && value) {
      return value;
    }
  }
  return null;
}
